import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import UseSurveyButton from "../../Interaction/UseSurveyButton";
import exportQuestions from "../exportQuestions";
import Alias from "../../User/Alias";
import BackButton from "../../Navbar/BackButton";
import { DRAWER_WIDTH } from "../../tools";
import ErrorMessage from "../Error/ErrorMessage";
import PhonePreview from "./PhonePreview";
import SaveButton from "../Save/SaveButton";
import OptionMenu from "./OptionMenu";
import useClasses from "../../useClasses";

const styles = (theme) => ({
  appBar: {
    zIndex: theme.zIndex.appBar - 1,
    background: `${theme.palette.background.default}`,
    //backdropFilter: "saturate(180%) blur(20px)",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: theme.palette.text.primary,
    paddingLeft: DRAWER_WIDTH,
    //borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
  },
  container: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  button: {
    marginRight: theme.spacing(2),
    color: theme.palette.text.secondary,
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  roundedButton: {
    marginRight: theme.spacing(2),
  },
});

function ElevationScroll(props) {
  const { children, window } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? window() : undefined,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
  });
}

ElevationScroll.propTypes = {
  children: PropTypes.element.isRequired,
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window: PropTypes.func,
};

export default function CustomToolbar({
  questionTree,
  selected,
  details,
  interactionId,
  handleQuestionTreeApi,
  setDetails,
  creator,
  setCreator,
  isBlocking,
  setIsBlocking,
  disabled,
  setLastSaved,
}) {
  const classes = useClasses(styles);
  const { t } = useTranslation();
  const [isError, setIsError] = useState(false); // error detected (prevent saving)
  const [openValidationDialog, setOpenValidationDialog] = useState(false);
  const handleOpenValidationDialog = () => {
    if (isError) setOpenValidationDialog(true);
  };
  const handleCloseValidationDialog = () => setOpenValidationDialog(false);

  const onSaveSuccess = (data) => {
    setIsBlocking(false);
    setLastSaved(data?.lastSaved ?? "");
  };

  return (
    <div>
      <ElevationScroll>
        <AppBar className={classes.appBar}>
          <Toolbar className={classes.toolbar}>
            <div className={classes.container}>
              <BackButton />
              {/* public interaction: show creator */}
              {typeof interactionId === "number" ? (
                <Typography variant="subtitle2">
                  {t("library.from")}: {creator.firstName} {creator.lastName} (@
                  {creator.code})
                </Typography>
              ) : null}
              {/* local interaction: show client */}
              {typeof interactionId === "string" ? (
                <Alias size="small" />
              ) : null}
            </div>
            <div className={classes.container}>
              {/* show question preview */}
              <PhonePreview
                questionTree={questionTree}
                selected={selected}
                details={details}
                handleQuestionTreeApi={handleQuestionTreeApi}
              />
              {/* show error messages */}
              <ErrorMessage
                questionTree={questionTree}
                details={details}
                handleQuestionTreeApi={handleQuestionTreeApi}
                openValidationDialog={openValidationDialog}
                handleCloseValidationDialog={handleCloseValidationDialog}
                isError={isError}
                setIsError={setIsError}
              />
              <OptionMenu
                details={details}
                handleQuestionTreeApi={handleQuestionTreeApi}
              />
              {/* use the survey for a particular client (only if a client is selected) */}
              <UseSurveyButton
                from="editor"
                interactionId={interactionId}
                questionList={exportQuestions(questionTree, details)}
                details={details}
                isError={isError}
                onError={handleOpenValidationDialog}
                onItemClick={(optionId) => {
                  if (["send", "schedule", "install"].includes(optionId))
                    // disable blocking
                    setIsBlocking(false);
                }}
                onSuccess={onSaveSuccess}
                disabled={disabled}
              />
              {/* save the survey */}
              <SaveButton
                questionList={exportQuestions(questionTree, details)}
                interactionId={interactionId}
                creator={creator}
                onClick={handleOpenValidationDialog}
                setCreator={setCreator}
                setDetails={setDetails}
                isBlocking={isBlocking}
                isError={isError}
                disabled={disabled}
                onSuccess={onSaveSuccess}
              />
            </div>
          </Toolbar>
        </AppBar>
      </ElevationScroll>
      <Toolbar />
    </div>
  );
}
